import React, { useRef } from 'react';
import type { InputRef } from 'antd';
import type { RefSelectProps } from 'antd/es/select';
import { AppButton, AppRadio, AppRadioGroup, AppSelect, AppTextField } from '@/components/atoms';
import { Search } from '@/components/atoms/AppIcon';
import { CollapsibleCard } from '@/components/molecules';
import { RA2NDatePicker } from '@/components/organisms';
import type { MassagerOption, RezeptInitParam, SearchRadio, SortRadio } from '@/types/rezept.types';
import { REZEPT_KBN_OPTIONS, SEARCH_RADIO_DISABLED_MAP, SORT_RADIO_OPTIONS } from '../constants';
import type { SearchState } from '../types';
import { toHalfWidthAnk, toHalfWidthDigits } from '../utils';
import RezeptPrintCountBadge from './RezeptPrintCountBadge';

interface Props {
  search: SearchState;
  param: RezeptInitParam | undefined;
  massagerOptions: MassagerOption[];
  printNum: number;
  resultCount: number;
  onChange: <K extends keyof SearchState>(key: K, value: SearchState[K]) => void;
  onChangeSearchRadio: (next: SearchRadio) => void;
  onSubmitSearch: () => void;
  onSubmitSyokan: () => void;
}

const RezeptSearchFilters: React.FC<Props> = ({
  search,
  param,
  massagerOptions,
  printNum,
  resultCount,
  onChange,
  onChangeSearchRadio,
  onSubmitSearch,
  onSubmitSyokan,
}) => {
  const disabledFields = SEARCH_RADIO_DISABLED_MAP[search.search_radio];
  const isDisabled = (key: keyof SearchState) => disabledFields.has(key);

  const insurerNameRef = useRef<InputRef>(null);
  const serialNumberRef = useRef<InputRef>(null);
  const insurerTypeRef = useRef<RefSelectProps>(null);
  const patientNameRef = useRef<InputRef>(null);

  const handleSearchRadioChange = (next: SearchRadio) => {
    onChangeSearchRadio(next);
    requestAnimationFrame(() => {
      switch (next) {
        case 1:
          insurerNameRef.current?.focus();
          break;
        case 2:
          serialNumberRef.current?.focus();
          break;
        case 3:
          insurerTypeRef.current?.focus();
          break;
        case 4:
          patientNameRef.current?.focus();
          break;
      }
    });
  };

  const rezeptTemplateOptions = [
    { value: '', label: 'すべて' },
    ...(param?.rezept_templates ?? []).map(tpl => ({
      value: String(tpl.id),
      label: tpl.name,
    })),
  ];

  const insurerTypeOptions = (param?.insurer_types ?? []).map(t => ({
    value: String(t.id),
    label: t.name,
  }));

  const iniOptions = [
    { value: '', label: 'すべて' },
    ...(param?.iniarr ?? []).map(i => ({ value: String(i.id), label: i.name })),
  ];

  const massagerSelectOptions = [
    { value: '', label: 'すべて' },
    ...massagerOptions.map(m => ({
      value: String(m.massager_id),
      label: `${m.serial_number}:${m.name}`,
    })),
  ];

  return (
    <CollapsibleCard title="レセプト発行" className="mb-4">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-x-4 gap-y-3 md:grid-cols-2 xl:grid-cols-5">
          <label className="flex min-w-0 flex-col gap-1 text-sm sm:flex-row sm:items-center sm:gap-2">
            <span className="whitespace-nowrap sm:min-w-[80px]">提出年月</span>
            <RA2NDatePicker
              picker="month"
              value={search.search_yearmonth || undefined}
              onChange={v => onChange('search_yearmonth', v || '')}
              placeholder="元号00年00月"
              className="w-full"
            />
          </label>

          <label className="flex min-w-0 flex-col gap-1 text-sm sm:flex-row sm:items-center sm:gap-2">
            <span className="whitespace-nowrap sm:min-w-[90px]">施術師</span>
            <AppSelect
              className="w-full"
              value={search.massager_id || ''}
              onChange={v => onChange('massager_id', (v ?? '') as string)}
              options={massagerSelectOptions}
            />
          </label>

          <label className="flex min-w-0 flex-col gap-1 text-sm sm:flex-row sm:items-center sm:gap-2">
            <span className="whitespace-nowrap sm:min-w-[70px]">施術年月</span>
            <RA2NDatePicker
              picker="month"
              value={search.search_visitmonth || undefined}
              onChange={v => onChange('search_visitmonth', v || '')}
              placeholder="元号00年00月"
              className="w-full"
            />
          </label>

          <label className="flex min-w-0 flex-col gap-1 text-sm sm:flex-row sm:items-center sm:gap-2">
            <span className="whitespace-nowrap sm:min-w-[50px]">用紙</span>
            <AppSelect
              className="w-full"
              value={search.rezept_template_id || ''}
              onChange={v => onChange('rezept_template_id', (v ?? '') as string)}
              options={rezeptTemplateOptions}
            />
          </label>

          <label className="flex min-w-0 flex-col gap-1 text-sm sm:flex-row sm:items-center sm:gap-2">
            <span className="whitespace-nowrap sm:min-w-[50px]">本・関</span>
            <AppSelect
              className="w-full"
              value={search.rezept_kbn || ''}
              onChange={v => onChange('rezept_kbn', (v ?? '') as string)}
              options={[...REZEPT_KBN_OPTIONS]}
            />
          </label>
        </div>

        <div className="p-3 border rounded">
          {/* HIT-4568: fixed-width fields (保険者種別 sized to 協会けんぽ, never shrink on
              resize) packed with flex-wrap so they keep their width and drop to the next
              line instead of squeezing / clipping the labels. */}
          <div className="flex flex-col gap-x-6 gap-y-3 sm:flex-row sm:flex-wrap sm:items-center">
            <div className="flex min-w-0 flex-col gap-2 text-sm sm:flex-row sm:items-center">
              <AppRadio
                checked={search.search_radio === 1}
                onChange={() => handleSearchRadioChange(1)}
                className="whitespace-nowrap"
              >
                保険者名
              </AppRadio>
              <AppTextField
                ref={insurerNameRef}
                value={search.insurer_name}
                onChange={e => onChange('insurer_name', e.target.value)}
                onBlur={e => onChange('insurer_name', toHalfWidthAnk(e.target.value))}
                disabled={isDisabled('insurer_name')}
                maxLength={225}
                className="w-full sm:w-40"
              />
            </div>

            <div className="flex min-w-0 flex-col gap-2 text-sm sm:flex-row sm:items-center">
              <AppRadio
                checked={search.search_radio === 2}
                onChange={() => handleSearchRadioChange(2)}
                className="whitespace-nowrap"
              >
                受診者番号
              </AppRadio>
              <AppTextField
                ref={serialNumberRef}
                value={search.serial_number}
                onChange={e => onChange('serial_number', e.target.value)}
                onBlur={e => onChange('serial_number', toHalfWidthDigits(e.target.value))}
                disabled={isDisabled('serial_number')}
                maxLength={6}
                inputMode="numeric"
                className="w-full sm:w-24"
              />
            </div>

            <div className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center">
              <AppRadio
                checked={search.search_radio === 3}
                onChange={() => handleSearchRadioChange(3)}
                className="whitespace-nowrap"
              >
                保険者種別
              </AppRadio>
              <AppSelect
                ref={insurerTypeRef}
                value={search.insurer_type_id || ''}
                onChange={v => onChange('insurer_type_id', (v ?? '') as string)}
                options={insurerTypeOptions}
                disabled={isDisabled('insurer_type_id')}
                className="w-full sm:w-36"
              />
              <span className="whitespace-nowrap">仮名頭文字</span>
              <AppSelect
                value={search.ini || ''}
                onChange={v => onChange('ini', (v ?? '') as string)}
                options={iniOptions}
                disabled={isDisabled('ini')}
                className="w-full sm:w-24"
              />
            </div>

            <div className="flex min-w-0 flex-col gap-2 text-sm sm:flex-row sm:items-center">
              <AppRadio
                checked={search.search_radio === 4}
                onChange={() => handleSearchRadioChange(4)}
                className="whitespace-nowrap"
              >
                受診者名
              </AppRadio>
              <AppTextField
                ref={patientNameRef}
                value={search.patient_name}
                onChange={e => onChange('patient_name', e.target.value)}
                onBlur={e => onChange('patient_name', toHalfWidthAnk(e.target.value))}
                disabled={isDisabled('patient_name')}
                maxLength={225}
                className="w-full sm:w-40"
              />
            </div>
          </div>
        </div>

        <div className="flex flex-col justify-between gap-3 xl:flex-row xl:items-center">
          <div className="flex min-w-0 flex-col gap-2 p-2 text-sm border rounded sm:flex-row sm:items-center sm:gap-3">
            <span className="shrink-0 whitespace-nowrap">並び順</span>
            <AppRadioGroup
              value={search.sort_radio}
              onChange={e => onChange('sort_radio', Number(e.target.value) as SortRadio)}
              className="flex flex-wrap gap-x-3 gap-y-1"
            >
              {SORT_RADIO_OPTIONS.map(opt => (
                <AppRadio key={opt.value} value={opt.value}>
                  {opt.label}
                </AppRadio>
              ))}
            </AppRadioGroup>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center xl:justify-end">
            <AppButton type="primary" onClick={onSubmitSearch} className="w-full sm:w-auto">
              <Search /> 検索
            </AppButton>
            <AppButton type="primary" onClick={onSubmitSyokan} className="w-full sm:w-auto">
              <Search /> 償還払い保険者
            </AppButton>
            <RezeptPrintCountBadge printNum={printNum} total={resultCount} />
          </div>
        </div>
      </div>
    </CollapsibleCard>
  );
};

// Memoised: the page re-renders on every row checkbox click (selectedIds), and none of these
// props depend on the selection.
export default React.memo(RezeptSearchFilters);
